import React from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  Spinner,
  Text,
  Flex,
  Badge,
} from '@chakra-ui/react';
import { FiChevronDown } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import qs from 'qs';
import xhr from 'xhr';
import { Link } from 'react-router-dom';

/**
 * Lista negra: estudiantes con black_list = true que ADEMÁS tienen al menos una
 * matrícula con status = 'estudiando', en grade-enrollment o technical-career-enrollment.
 * Se muestra una fila por matrícula con: código (id del estudiante), nombre,
 * celular, grado/carrera y el motivo (black_list_description). El email NO se
 * muestra en la tabla, solo se incluye en el Excel. Se puede filtrar por uno o
 * varios tipos de programa y descargar el resultado en Excel.
 */
function BlackList() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  // Filtro multi-selección: array de tipos de programa. Vacío = todos.
  const [selectedTypes, setSelectedTypes] = React.useState([]);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // Matrículas con status 'estudiando' de estudiantes en lista negra
        // (filtro por la relación student).
        const buildQuery = (programKey) =>
          qs.stringify(
            {
              filters: {
                status: { $in: ['estudiando', 'promocion_pendiente'] },
                student: { black_list: { $eq: true } },
              },
              populate: {
                student: { populate: ['user'] },
                [programKey]: { populate: ['program_type'] },
              },
              pagination: { limit: -1 },
            },
            { encodeValuesOnly: true }
          );

        const [gradeRes, techRes] = await Promise.all([
          xhr(`/grade-enrollments?${buildQuery('grade')}`),
          xhr(`/technical-career-enrollments?${buildQuery('technical_career')}`),
        ]);

        const mapRow = (enr, program, source) => {
          const student = enr?.student;
          if (!student) return null;
          // Nombre de la relación propia: grado (grade-enrollment) o
          // carrera técnica (technical-career-enrollment).
          const programName = program?.name || '—';
          // Tipo de programa (para el filtro).
          const programType = program?.program_type?.name || 'Sin tipo de programa';
          const name = `${student.user?.first_name || ''} ${student.user?.last_name || ''}`.trim();
          return {
            key: `${source}-${enr.id}`,
            studentId: student.id,
            name: name || 'Sin nombre',
            phone: student.user?.cellphone || student.home_phone || '',
            email: student.user?.email || '',
            reason: student.black_list_description || '',
            programName,
            programType,
          };
        };

        const gradeRows = (gradeRes?.data?.data || [])
          .map((enr) => mapRow(enr, enr.grade, 'g'))
          .filter(Boolean);
        const techRows = (techRes?.data?.data || [])
          .map((enr) => mapRow(enr, enr.technical_career, 't'))
          .filter(Boolean);

        setRows([...gradeRows, ...techRows]);
      } catch (error) {
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Tipos de programa presentes en los resultados (para el filtro).
  const programTypes = React.useMemo(() => {
    const set = new Set(rows.map((r) => r.programType).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [rows]);

  const filteredRows =
    selectedTypes.length === 0
      ? rows
      : rows.filter((r) => selectedTypes.includes(r.programType));

  const handleDownloadExcel = () => {
    const data = filteredRows.map((r) => ({
      'Código': r.studentId,
      Nombre: r.name,
      Celular: r.phone || '',
      Email: r.email || '',
      'Grado / Carrera': r.programName,
      'Tipo de programa': r.programType,
      Motivo: r.reason || '',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Lista negra');
    // Generar el archivo como ArrayBuffer y descargarlo con un enlace temporal
    // (XLSX.writeFile no dispara la descarga en este entorno CRA).
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lista-negra.xlsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const filterLabel =
    selectedTypes.length === 0
      ? 'Todos'
      : `${selectedTypes.length} seleccionado(s)`;

  return (
    <Box p="10px">
      <Heading size="md" mb="4px">
        Lista negra
      </Heading>
      <Text fontSize="sm" color="gray.500" mb="12px">
        Estudiantes en lista negra que tienen al menos una matrícula activa.
      </Text>

      <Flex align="center" gap="12px" mb="12px" flexWrap="wrap">
        <Text fontSize="sm" fontWeight="600">
          Tipo de programa:
        </Text>
        <Menu closeOnSelect={false}>
          <MenuButton
            as={Button}
            size="sm"
            variant="outline"
            rightIcon={<FiChevronDown />}
            minW="220px"
            textAlign="left"
          >
            {filterLabel}
          </MenuButton>
          <MenuList maxH="300px" overflowY="auto">
            <MenuOptionGroup
              type="checkbox"
              value={selectedTypes}
              onChange={(val) => setSelectedTypes(val)}
            >
              {programTypes.map((pt) => (
                <MenuItemOption key={pt} value={pt}>
                  {pt}
                </MenuItemOption>
              ))}
            </MenuOptionGroup>
          </MenuList>
        </Menu>
        {selectedTypes.length > 0 && (
          <Button size="xs" variant="ghost" onClick={() => setSelectedTypes([])}>
            Limpiar
          </Button>
        )}
        <Badge colorScheme="purple">{filteredRows.length} estudiante(s)</Badge>
        <Button
          size="sm"
          colorScheme="green"
          onClick={handleDownloadExcel}
          isDisabled={filteredRows.length === 0}
        >
          Descargar Excel
        </Button>
      </Flex>

      {loading ? (
        <Flex align="center" gap="8px" py="20px">
          <Spinner size="sm" /> <Text>Cargando...</Text>
        </Flex>
      ) : (
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead bg="gray.200">
              <Tr>
                <Th>Código</Th>
                <Th>Nombre</Th>
                <Th>Celular</Th>
                <Th>Grado / Carrera</Th>
                <Th>Motivo</Th>
                <Th>Ver</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredRows.length === 0 ? (
                <Tr>
                  <Td colSpan={6}>
                    <Text py="12px" color="gray.500">
                      No hay estudiantes en lista negra con matrícula activa
                      {selectedTypes.length > 0
                        ? ' para los tipos de programa seleccionados.'
                        : '.'}
                    </Text>
                  </Td>
                </Tr>
              ) : (
                filteredRows.map((row) => (
                  <Tr key={row.key}>
                    <Td>{row.studentId}</Td>
                    <Td>{row.name}</Td>
                    <Td>{row.phone || '—'}</Td>
                    <Td>{row.programName}</Td>
                    <Td whiteSpace="normal" maxW="360px">
                      {row.reason || '—'}
                    </Td>
                    <Td>
                      <Link
                        to={`/plataforma/administrador/ver-estudiante/${row.studentId}`}
                      >
                        Ver
                      </Link>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default BlackList;
